import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FolderKanban, LayoutTemplate, Building2, BarChart3,
  Settings, LogOut, Compass, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/templates", label: "Discovery Templates", icon: LayoutTemplate, testid: "nav-templates" },
  { to: "/customers", label: "Customers", icon: Building2, testid: "nav-customers" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/administration", label: "Administration", icon: Settings, testid: "nav-administration", roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Administrator", consultant: "Consultant", customer: "Customer" };

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="brand-gradient flex h-10 w-10 items-center justify-center rounded-xl shadow-sm">
            <Compass className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-[17px] font-extrabold leading-none tracking-tight text-slate-900">Inspiro Discover</div>
            <div className="mt-1 text-[11px] font-medium text-slate-400">by InspiroWorks</div>
          </div>
        </div>

        <nav className="mt-2 flex-1 space-y-1 px-4">
          {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
                  isActive ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon className={cn("h-[18px] w-[18px]", isActive ? "text-sky-600" : "text-slate-400 group-hover:text-slate-600")} strokeWidth={2.2} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-4">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-800">{user?.name}</div>
              <div className="text-[11px] font-medium text-sky-600">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-h-screen flex-1 flex-col lg:pl-[264px]">
        <Outlet />
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <div className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
      <div className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-10">
        <div>
          {breadcrumb && (
            <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-400">
              {breadcrumb.map((b, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3" />}
                  <span className={i === breadcrumb.length - 1 ? "text-slate-600" : ""}>{b}</span>
                </span>
              ))}
            </div>
          )}
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}
